import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Sidebar from "../Dashboard/Sidebar";
import { lookupPatientByPatientId } from "../../actions/patientActions";
import { createBill } from "../../actions/billingActions";
import { clearBillCreated, clearBillingError } from "../../slices/billingSlice";
import { getTreatments } from "../../actions/treatmentActions";
import { getAllStaff } from "../../actions/staffActions";
import AppDatePicker from "../common/AppDatePicker";

const currency = (n) =>
    `\u20b9${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const emptyItem = () => ({
    treatment: "",
    description: "",
    quantity: 1,
    unitPrice: 0
});

const emptyPatientDetails = () => ({
    _id: "",
    patientId: "",
    name: "",
    age: "",
    gender: "",
    phone: "",
    address: ""
});

const toPatientDetails = (patient) => ({
    _id: patient._id,
    patientId: patient.patientId,
    name: patient.name || "",
    age: patient.age || "",
    gender: patient.gender || "",
    phone: patient.phone || "",
    address: patient.address || ""
});

export default function NewBill() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { bill, loading, error, isBillCreated } = useSelector((state) => state.billingState);
    const { treatments } = useSelector((state) => state.treatmentState);
    const { staffList } = useSelector((state) => state.staffState);

    useEffect(() => {
        dispatch(getTreatments());
        dispatch(getAllStaff());
    }, [dispatch]);

    // when arriving from a patient's page, their details come along via
    // route state so we don't have to look them up again
    const incomingPatient = location.state?.patient;

    const [patientIdInput, setPatientIdInput] = useState("");
    const [lookingUp, setLookingUp] = useState(false);
    const [patientFound, setPatientFound] = useState(!!incomingPatient?._id);
    const [patientDetails, setPatientDetails] = useState(
        incomingPatient?._id ? toPatientDetails(incomingPatient) : emptyPatientDetails()
    );

    const getTodayDate = () => {
        const today = new Date();
        const offset = today.getTimezoneOffset();
        return new Date(today.getTime() - offset * 60000)
            .toISOString()
            .split("T")[0];
    };

    const [doctor, setDoctor] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("Cash");

    const [customInvoiceNumber, setCustomInvoiceNumber] = useState("");
    const [invoiceDate, setInvoiceDate] = useState(getTodayDate());
    const [items, setItems] = useState([emptyItem()]);
    const [discount, setDiscount] = useState(0);
    const [tax, setTax] = useState(0);
    const [notes, setNotes] = useState("");

    // 1. Always reset bill creation status on mount and unmount to prevent stale success view
    useEffect(() => {
        dispatch(clearBillCreated());
        dispatch(getTreatments());
        dispatch(getAllStaff());
        return () => {
            dispatch(clearBillCreated());
        };
    }, [dispatch]);

    // 2. Sync incoming patient if location state changes
    useEffect(() => {
        if (incomingPatient?._id) {
            setPatientDetails(toPatientDetails(incomingPatient));
            setPatientFound(true);
        }
    }, [incomingPatient]);

    // Auto-select doctor if only 1 doctor exists
    useEffect(() => {
        const doctors = (staffList || []).filter(
            (s) => s.designation && s.designation.toLowerCase().includes("doctor")
        );
        if (!doctor && doctors.length === 1) {
            setDoctor(doctors[0].name);
        }
    }, [staffList, doctor]);

    // Auto-select treatment if only 1 treatment exists
    useEffect(() => {
        if (treatments && treatments.length === 1 && items.length === 1 && !items[0].treatment) {
            updateItem(0, "treatment", treatments[0].name);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [treatments]);

    // 3. Error toast & clear
    useEffect(() => {
        if (error) {
            toast(error, { type: "error" });
            dispatch(clearBillingError());
        }
    }, [error, dispatch]);

    // 4. Success toast once bill is created
    useEffect(() => {
        if (isBillCreated && bill?._id) {
            toast(`Bill ${bill.invoiceNumber || ""} created successfully`, { type: "success" });
        }
    }, [isBillCreated, bill]);

    const handleLookup = async () => {
        let clean = patientIdInput.trim();
        if (!clean) {
            toast("Enter a patient ID or number first", { type: "error" });
            return;
        }
        try {
            setLookingUp(true);
            const patient = await lookupPatientByPatientId(clean);
            setPatientDetails(toPatientDetails(patient));
            setPatientFound(true);
            toast(`Loaded details for ${patient.name}`, { type: "success" });
        } catch (err) {
            setPatientFound(false);
            toast(err?.response?.data?.message || "Patient not found", { type: "error" });
        } finally {
            setLookingUp(false);
        }
    };

    const clearPatient = () => {
        setPatientFound(false);
        setPatientDetails(emptyPatientDetails());
        setPatientIdInput("");
    };

    const updateItem = (index, field, value) => {
        if (field === "treatment" && value) {
            const isDuplicate = items.some(
                (other, oIdx) => oIdx !== index && (
                    (other.treatment && other.treatment.trim().toLowerCase() === value.trim().toLowerCase()) ||
                    (other.description && other.description.trim().toLowerCase() === value.trim().toLowerCase())
                )
            );
            if (isDuplicate) {
                toast(`"${value}" is already added to this bill`, { type: "warning" });
                return;
            }
        }

        setItems((prev) =>
            prev.map((it, i) => {
                if (i !== index) return it;
                const updated = { ...it, [field]: value };

                // picking a treatment auto-fills description + price;
                // both can still be edited manually afterwards
                if (field === "treatment") {
                    const match = treatments?.find((t) => t.name === value);
                    updated.description = value;
                    if (match) updated.unitPrice = match.price;
                }
                return updated;
            })
        );
    };

    const addItemRow = () => setItems((prev) => [...prev, emptyItem()]);
    const removeItemRow = (index) =>
        setItems((prev) => prev.filter((_, i) => i !== index));

    const subtotal = items.reduce(
        (sum, it) => sum + (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0),
        0
    );
    const estimatedTotal = subtotal - (Number(discount) || 0) + (Number(tax) || 0);

    const resetForm = () => {
        clearPatient();
        setCustomInvoiceNumber("");
        setInvoiceDate(getTodayDate());
        setItems([emptyItem()]);
        setDiscount(0);
        setTax(0);
        setNotes("");
        setDoctor("");
        setPaymentMethod("Cash");
        dispatch(clearBillCreated());
    };

    const submitBill = (e) => {
        e.preventDefault();

        if (!patientDetails._id) {
            toast("Look up a valid patient ID before creating a bill", { type: "error" });
            return;
        }

        const cleanItems = items
            .filter((it) => it.description && it.unitPrice !== "")
            .map((it) => ({
                description: it.description,
                quantity: Number(it.quantity) || 1,
                unitPrice: Number(it.unitPrice)
            }));

        if (cleanItems.length === 0) {
            toast("Add at least one treatment / item", { type: "error" });
            return;
        }

        if (!doctor) {
            toast("Please select a consulting doctor", { type: "error" });
            return;
        }

        dispatch(
            createBill({
                patientId: patientDetails._id,
                doctor,
                paymentMethod,
                items: cleanItems,
                customInvoiceNumber,
                discount: Number(discount) || 0,
                tax: Number(tax) || 0,
                notes,
                date: invoiceDate
            })
        );
    };

    return (
        <div className="row g-0">
      <div className="col-12 col-md-2"><Sidebar /></div>
      <div className="col-12 col-md-10 main-content">
                <h1 className="my-4">New Bill</h1>

                {isBillCreated && bill?._id ? (
                    // ---------- success view ----------
                    <div className="border rounded p-4">
                        <h5>Bill {bill.invoiceNumber} created successfully</h5>
                        <p className="text-muted mb-3">
                            Total: {currency(bill.totalAmount)}
                        </p>
                        <button className="btn btn-outline-secondary me-2" onClick={resetForm}>
                            Create Another Bill
                        </button>
                        <button
                            className="btn btn-outline-secondary"
                            onClick={() => {
                                const pId = patientDetails.patientId || patientDetails._id || bill.patient?.patientId || bill.patientId;
                                dispatch(clearBillCreated());
                                if (pId) navigate(`/patient/${pId}`);
                                else navigate('/admin/billing');
                            }}
                        >
                            Go to Patient
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submitBill}>
                        {/* ---------- Patient lookup / details ---------- */}
                        <div className="border rounded p-3 mb-3">
                            <h6>Patient</h6>

                            {!patientFound ? (
                                <div className="row g-2 align-items-end mb-2">
                                    <div className="col-12 col-sm-6 col-md-5">
                                        <label className="form-label small mb-1 fw-semibold">Patient ID</label>
                                        <div className="input-group">
                                            <span className="input-group-text fw-bold bg-light text-muted">GN-</span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="e.g. 5 or 0005"
                                                value={patientIdInput}
                                                onChange={(e) => {
                                                    let val = e.target.value;
                                                    // Strip leading GN- if pasted or typed
                                                    val = val.replace(/^(GN[\s\-_]*)?/i, '');
                                                    setPatientIdInput(val);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        handleLookup();
                                                    }
                                                }}
                                                autoFocus
                                            />
                                        </div>
                                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                            Enter number only (e.g. 5) & press Enter
                                        </small>
                                    </div>
                                    <div className="col-12 col-sm-4 col-md-3">
                                        <button
                                            type="button"
                                            className="btn btn-secondary w-100"
                                            onClick={handleLookup}
                                            disabled={lookingUp}
                                        >
                                            {lookingUp ? "Looking up..." : "Find Patient"}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <Fragment>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="badge bg-success">
                                            Patient details fetched automatically for {patientDetails.patientId}
                                        </span>
                                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearPatient}>
                                            Change patient
                                        </button>
                                    </div>

                                    {/* fetched from the patient record - shown for reference only, not editable here */}
                                    <div className="row g-2">
                                        <div className="col-4">
                                            <label className="form-label small mb-0 text-muted">Name</label>
                                            <div className="form-control-plaintext fw-semibold">{patientDetails.name}</div>
                                        </div>
                                        <div className="col-2">
                                            <label className="form-label small mb-0 text-muted">Age</label>
                                            <div className="form-control-plaintext">{patientDetails.age}</div>
                                        </div>
                                        <div className="col-2">
                                            <label className="form-label small mb-0 text-muted">Gender</label>
                                            <div className="form-control-plaintext">{patientDetails.gender}</div>
                                        </div>
                                        <div className="col-4">
                                            <label className="form-label small mb-0 text-muted">Phone</label>
                                            <div className="form-control-plaintext">{patientDetails.phone}</div>
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label small mb-0 text-muted">Address</label>
                                            <div className="form-control-plaintext">{patientDetails.address}</div>
                                        </div>
                                    </div>
                                    <p className="text-muted small mt-2 mb-0">
                                        To change these details, update the patient's record from their patient page.
                                    </p>
                                </Fragment>
                            )}
                        </div>

                        {/* ---------- Doctor Selection, Payment Method & Invoice Info ---------- */}
                        <div className="border rounded p-3 mb-3">
                            <div className="row g-3">
                                <div className="col-md-3">
                                    <label className="form-label mb-1">Invoice Date</label>
                                    <AppDatePicker
                                        label="Invoice Date"
                                        value={invoiceDate}
                                        onChange={(val) => setInvoiceDate(val)}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label mb-1">Consulting Doctor *</label>
                                    <select
                                        className="form-control"
                                        value={doctor}
                                        onChange={(e) => setDoctor(e.target.value)}
                                    >
                                        <option value="">-- Select Doctor --</option>
                                        {staffList
                                            ?.filter(s => s.designation && s.designation.toLowerCase().includes('doctor'))
                                            .map(s => (
                                                <option key={s._id} value={s.name}>{s.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label mb-1">Payment Method</label>
                                    <select
                                        className="form-control"
                                        value={paymentMethod}
                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                    >
                                        <option value="Cash">Cash</option>
                                        <option value="Card">Card</option>
                                        <option value="UPI">UPI</option>
                                        <option value="Bank Transfer">Bank Transfer</option>
                                        <option value="Insurance">Insurance</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label mb-1">Invoice Number (Optional)</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Leave blank to auto-generate"
                                        value={customInvoiceNumber}
                                        onChange={(e) => setCustomInvoiceNumber(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* ---------- Treatments / items ---------- */}
                        <div className="border rounded p-3 mb-3">
                            <h6>Treatments / Charges</h6>

                            {items.map((it, index) => (
                                <div className="row g-2 align-items-center mb-2" key={index}>
                                    <div className="col-4">
                                        <select
                                            className="form-select"
                                            value={it.treatment}
                                            onChange={(e) => updateItem(index, "treatment", e.target.value)}
                                        >
                                            <option value="">Select Treatment</option>
                                            {treatments?.map((t) => {
                                                const isAlreadySelected = items.some(
                                                    (other, oIdx) => oIdx !== index && (
                                                        (other.treatment && other.treatment.trim().toLowerCase() === t.name.trim().toLowerCase()) ||
                                                        (other.description && other.description.trim().toLowerCase() === t.name.trim().toLowerCase())
                                                    )
                                                );
                                                return (
                                                    <option key={t.name} value={t.name} disabled={isAlreadySelected}>
                                                        {t.name}{isAlreadySelected ? " (Already added)" : ""}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                    <div className="col-3">
                                        <input
                                            className="form-control"
                                            placeholder="Description (editable)"
                                            value={it.description}
                                            onChange={(e) => updateItem(index, "description", e.target.value)}
                                        />
                                    </div>
                                    <div className="col-1">
                                        <input
                                            type="number"
                                            min="1"
                                            className="form-control"
                                            placeholder="Qty"
                                            value={it.quantity}
                                            onChange={(e) => updateItem(index, "quantity", e.target.value)}
                                        />
                                    </div>
                                    <div className="col-2">
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            className="form-control"
                                            placeholder="Price"
                                            value={it.unitPrice}
                                            onChange={(e) => updateItem(index, "unitPrice", e.target.value)}
                                        />
                                    </div>
                                    <div className="col-1 text-end">
                                        {currency((Number(it.quantity) || 0) * (Number(it.unitPrice) || 0))}
                                    </div>
                                    <div className="col-1">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger w-100"
                                            onClick={() => removeItemRow(index)}
                                            disabled={items.length === 1}
                                        >
                                            &times;
                                        </button>
                                    </div>
                                </div>
                            ))}

                            <button type="button" className="btn btn-sm btn-outline-secondary mt-2" onClick={addItemRow}>
                                + Add another treatment
                            </button>
                        </div>

                        {/* ---------- Discount / tax / totals ---------- */}
                        <div className="border rounded p-3 mb-3">
                            <div className="row g-3">
                                <div className="col-3">
                                    <label className="form-label small mb-0">Discount (manual)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        className="form-control"
                                        value={discount}
                                        onChange={(e) => setDiscount(e.target.value)}
                                    />
                                </div>
                                <div className="col-3">
                                    <label className="form-label small mb-0">Tax</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        className="form-control"
                                        value={tax}
                                        onChange={(e) => setTax(e.target.value)}
                                    />
                                </div>
                                <div className="col-6">
                                    <label className="form-label small mb-0">Notes</label>
                                    <input
                                        className="form-control"
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="text-end mt-3">
                                <div className="text-muted">Subtotal: {currency(subtotal)}</div>
                                <h5>Total: {currency(estimatedTotal)}</h5>
                            </div>
                        </div>

                        <button className="btn btn-primary" type="submit" disabled={loading}>
                            {loading ? "Saving..." : "Create Bill"}
                        </button>
                    </form>
                )}
        </div>
        </div>
    );
}



